import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import ActionMenu from '@/components/base/ActionMenu';

interface AdminUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  full_name: string;
  role: string;
  plan: string;
  credits: number;
  monthly_message_limit: number;
  messages_sent_this_month: number;
  onboarding_completed: boolean;
  connection_type: string | null;
}

const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial',
  starter: 'Starter',
  professional: 'Professional',
  business: 'Business',
  enterprise: 'Enterprise',
};

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400' },
  user: { label: 'Usuário', color: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-500/10 dark:text-secondary-400' },
  manager: { label: 'Gerente', color: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400' },
  operator: { label: 'Operador', color: 'bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400' },
};

const SUPER_ADMIN_EMAIL = 'nx3designer@gmail.com';

export default function AdminUsersPage() {
  const { profile, user } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<AdminUser | null>(null);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editPlan, setEditPlan] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Add user modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [addFullName, setAddFullName] = useState('');
  const [addRole, setAddRole] = useState('user');
  const [addPlan, setAddPlan] = useState('trial');
  const [adding, setAdding] = useState(false);

  // Approve state
  const [approvingId, setApprovingId] = useState<string | null>(null);

  // Search
  const [search, setSearch] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('admin-manage-users', {
        method: 'GET',
      });
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);
      setUsers(data.users || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar usuários';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  if (profile && profile.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const openEdit = (u: AdminUser) => {
    setEditingUser(u);
    setEditRole(u.role);
    setEditPlan(u.plan);
    setSuccessMsg('');
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditRole('');
    setEditPlan('');
  };

  const handleSave = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        `admin-manage-users/${editingUser.id}`,
        {
          method: 'PUT',
          body: { role: editRole, plan: editPlan },
        },
      );
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);
      setSuccessMsg(`Usuário "${editingUser.full_name || editingUser.email}" atualizado!`);
      setEditingUser(null);
      fetchUsers();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar';
      setError(msg);
    } finally {
      setSaving(false);
      setTimeout(() => setSuccessMsg(''), 4000);
      setTimeout(() => setError(''), 4000);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        `admin-manage-users/${deleteConfirm.id}`,
        { method: 'DELETE' },
      );
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);
      setSuccessMsg(`Usuário "${deleteConfirm.full_name || deleteConfirm.email}" excluído.`);
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao excluir';
      setError(msg);
    } finally {
      setDeleting(false);
      setTimeout(() => setSuccessMsg(''), 4000);
      setTimeout(() => setError(''), 4000);
    }
  };

  const handleApprove = async (u: AdminUser) => {
    setApprovingId(u.id);
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        `admin-manage-users/${u.id}?action=approve`,
        { method: 'PATCH' },
      );
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);
      setSuccessMsg(`Usuário "${u.full_name || u.email}" aprovado!`);
      fetchUsers();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao aprovar';
      setError(msg);
    } finally {
      setApprovingId(null);
      setTimeout(() => setSuccessMsg(''), 4000);
      setTimeout(() => setError(''), 4000);
    }
  };

  const handleAddUser = async () => {
    if (!addEmail || !addPassword) {
      setError('Email e senha são obrigatórios');
      return;
    }
    setAdding(true);
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('admin-manage-users', {
        method: 'POST',
        body: {
          email: addEmail,
          password: addPassword,
          full_name: addFullName,
          role: addRole,
          plan: addPlan,
        },
      });
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);
      setSuccessMsg(`Usuário "${addFullName || addEmail}" criado com sucesso!`);
      setAddModalOpen(false);
      setAddEmail('');
      setAddPassword('');
      setAddFullName('');
      setAddRole('user');
      setAddPlan('trial');
      fetchUsers();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao criar usuário';
      setError(msg);
    } finally {
      setAdding(false);
      setTimeout(() => setSuccessMsg(''), 4000);
      setTimeout(() => setError(''), 4000);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return 'Nunca';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const filteredUsers = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      u.full_name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (ROLE_LABELS[u.role]?.label || u.role).toLowerCase().includes(q) ||
      (PLAN_LABELS[u.plan] || u.plan).toLowerCase().includes(q)
    );
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-1">
            Gerenciar Usuários
          </h1>
          <p className="text-sm text-foreground-500 dark:text-foreground-400">
            Visualize, edite, aprove e gerencie usuários do sistema.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-foreground-500 whitespace-nowrap">
            {users.length} usuário{users.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="px-3 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-40"
          >
            <i className={`ri-refresh-line text-base ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button
            onClick={() => { setAddModalOpen(true); setError(''); }}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-add-line text-base" />
            Adicionar
          </button>
        </div>
      </div>

      {/* Messages */}
      {successMsg && (
        <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-check-line text-emerald-500 text-lg" />
          <p className="text-sm text-emerald-700 dark:text-emerald-400">{successMsg}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-500 text-lg" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome, email, cargo ou plano..."
          className="w-full pl-9 pr-4 py-2.5 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
        />
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-foreground-500">Carregando usuários...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 dark:border-background-200">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">
                    Email
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    Plano
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    Cargo
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden lg:table-cell">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden lg:table-cell">
                    Cadastro
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden xl:table-cell">
                    Último acesso
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider w-12">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const roleInfo = ROLE_LABELS[u.role] || ROLE_LABELS.user;
                  const isPending = !u.email_confirmed_at;
                  const needsApproval = isPending || !u.onboarding_completed;

                  const menuActions = [
                    { label: 'Editar', icon: 'ri-edit-line', iconColor: 'text-primary-500', onClick: () => openEdit(u) },
                  ];

                  if (needsApproval) {
                    menuActions.push({
                      label: approvingId === u.id ? 'Aprovando...' : 'Aprovar',
                      icon: approvingId === u.id ? 'ri-loader-4-line animate-spin' : 'ri-check-double-line',
                      iconColor: 'text-emerald-500',
                      onClick: () => handleApprove(u),
                      disabled: approvingId === u.id,
                    });
                  }

                  menuActions.push({
                    label: 'Excluir',
                    icon: 'ri-delete-bin-line',
                    danger: true,
                    onClick: () => setDeleteConfirm(u),
                  });

                  return (
                    <tr
                      key={u.id}
                      className="border-b border-background-50 dark:border-background-200 hover:bg-background-50/60 dark:hover:bg-background-200/50 transition-colors"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                            u.role === 'admin'
                              ? 'bg-red-100 dark:bg-red-500/10 text-red-600 dark:text-red-400'
                              : 'bg-background-100 dark:bg-background-200 text-foreground-500 dark:text-foreground-400'
                          }`}>
                            {(u.full_name || u.email).split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-foreground-900 dark:text-foreground-100 truncate max-w-[150px]">
                              {u.full_name || 'Sem nome'}
                            </p>
                            <p className="text-[11px] text-foreground-400 md:hidden truncate max-w-[120px]">
                              {u.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-foreground-600 dark:text-foreground-400 hidden md:table-cell max-w-[200px] truncate">
                        {u.email}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${
                          u.plan === 'enterprise'
                            ? 'bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400'
                            : u.plan === 'professional'
                            ? 'bg-accent-100 dark:bg-accent-500/10 text-accent-700 dark:text-accent-400'
                            : 'bg-background-100 dark:bg-background-200 text-foreground-600 dark:text-foreground-400'
                        }`}>
                          {PLAN_LABELS[u.plan] || u.plan}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium ${roleInfo.color}`}>
                          {roleInfo.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        {isPending ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400">
                            <i className="ri-time-line text-[10px]" />
                            Pendente
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
                            <i className="ri-check-line text-[10px]" />
                            Ativo
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-foreground-600 dark:text-foreground-400 text-[13px] hidden lg:table-cell">
                        {formatDate(u.created_at)}
                      </td>
                      <td className="px-5 py-3.5 text-foreground-600 dark:text-foreground-400 text-[13px] hidden xl:table-cell">
                        {formatDateTime(u.last_sign_in_at)}
                      </td>
                      <td className="px-2 py-3.5 text-center">
                        <ActionMenu actions={menuActions} align="right" />
                      </td>
                    </tr>
                  );
                })}
                {filteredUsers.length === 0 && !loading && (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center">
                      {search ? (
                        <>
                          <i className="ri-search-line text-4xl text-foreground-300 mb-3 block" />
                          <p className="text-sm text-foreground-500">Nenhum resultado para "{search}"</p>
                          <p className="text-xs text-foreground-400 mt-1">Tente outro termo de busca</p>
                        </>
                      ) : (
                        <>
                          <i className="ri-user-search-line text-4xl text-foreground-300 mb-3 block" />
                          <p className="text-sm text-foreground-500">Nenhum usuário encontrado</p>
                          <p className="text-xs text-foreground-400 mt-1">Os usuários cadastrados aparecerão aqui</p>
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={cancelEdit} />
          <div className="relative bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 w-full max-w-[440px] mx-4 p-6 animate-fadeIn">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">
                Editar Usuário
              </h3>
              <button
                onClick={cancelEdit}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 hover:text-foreground-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 dark:bg-background-200 mb-5">
              <div className="w-10 h-10 rounded-xl bg-background-100 dark:bg-background-300 flex items-center justify-center text-sm font-bold text-foreground-600 dark:text-foreground-300 flex-shrink-0">
                {(editingUser.full_name || editingUser.email).split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100 truncate">
                  {editingUser.full_name || 'Sem nome'}
                </p>
                <p className="text-xs text-foreground-500 truncate">{editingUser.email}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-2">
                  Cargo
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['admin', 'user', 'manager', 'operator'].map((r) => {
                    const info = ROLE_LABELS[r] || ROLE_LABELS.user;
                    const isDisabled = r === 'admin' && user?.email !== SUPER_ADMIN_EMAIL;
                    return (
                      <button
                        key={r}
                        onClick={() => !isDisabled && setEditRole(r)}
                        className={`px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all border ${
                          isDisabled
                            ? 'border-background-200 dark:border-background-300 bg-background-50 dark:bg-background-200 text-foreground-300 dark:text-foreground-500 cursor-not-allowed opacity-50'
                            : editRole === r
                            ? 'border-primary-400 bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 cursor-pointer'
                            : 'border-background-200 dark:border-background-300 bg-white dark:bg-background-100 text-foreground-600 dark:text-foreground-400 hover:border-background-300 cursor-pointer'
                        }`}
                        disabled={isDisabled}
                      >
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium mb-1 ${info.color}`}>
                          {info.label}
                        </span>
                        {isDisabled && <i className="ri-lock-line text-[10px] ml-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-2">
                  Plano
                </label>
                <select
                  value={editPlan}
                  onChange={(e) => setEditPlan(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
                >
                  {Object.entries(PLAN_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-background-100 dark:border-background-200">
              <button
                onClick={cancelEdit}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || (editRole === editingUser.role && editPlan === editingUser.plan)}
                className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-40 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer ml-auto flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line" />
                    Salvar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 w-full max-w-[420px] mx-4 p-6 animate-fadeIn">
            <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-red-500" />
            </div>
            <h3 className="text-base font-heading font-semibold text-foreground-950 dark:text-foreground-100 text-center mb-2">
              Excluir usuário
            </h3>
            <p className="text-sm text-foreground-500 text-center mb-2">
              Tem certeza que deseja excluir permanentemente este usuário?
            </p>
            <div className="flex items-center gap-2.5 justify-center mb-4 p-3 rounded-xl bg-background-50 dark:bg-background-200">
              <div className="w-8 h-8 rounded-lg bg-background-100 dark:bg-background-300 flex items-center justify-center text-xs font-bold text-foreground-500">
                {(deleteConfirm.full_name || deleteConfirm.email).split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-800 dark:text-foreground-200">
                  {deleteConfirm.full_name || 'Sem nome'}
                </p>
                <p className="text-xs text-foreground-500">{deleteConfirm.email}</p>
              </div>
            </div>
            <p className="text-xs text-foreground-400 text-center mb-6">
              Essa ação é irreversível. O usuário perderá acesso à plataforma e todos os dados associados.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  'Excluir'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { if (!adding) setAddModalOpen(false); }} />
          <div className="relative bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 w-full max-w-[480px] mx-4 p-6 animate-fadeIn max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">
                Adicionar Usuário
              </h3>
              <button
                onClick={() => { if (!adding) setAddModalOpen(false); }}
                disabled={adding}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 hover:text-foreground-600 transition-colors cursor-pointer disabled:opacity-40"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-1.5">
                  Nome completo
                </label>
                <input
                  type="text"
                  value={addFullName}
                  onChange={(e) => setAddFullName(e.target.value)}
                  placeholder="Ex: João Silva"
                  className="w-full px-3.5 py-2.5 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-1.5">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="usuario@email.com"
                  className="w-full px-3.5 py-2.5 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-1.5">
                  Senha <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={addPassword}
                  onChange={(e) => setAddPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full px-3.5 py-2.5 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-2">
                  Cargo
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['admin', 'user', 'manager', 'operator'].map((r) => {
                    const info = ROLE_LABELS[r] || ROLE_LABELS.user;
                    const isDisabled = r === 'admin' && user?.email !== SUPER_ADMIN_EMAIL;
                    return (
                      <button
                        key={r}
                        onClick={() => !isDisabled && setAddRole(r)}
                        className={`px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all border ${
                          isDisabled
                            ? 'border-background-200 dark:border-background-300 bg-background-50 dark:bg-background-200 text-foreground-300 dark:text-foreground-500 cursor-not-allowed opacity-50'
                            : addRole === r
                            ? 'border-primary-400 bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 cursor-pointer'
                            : 'border-background-200 dark:border-background-300 bg-white dark:bg-background-100 text-foreground-600 dark:text-foreground-400 hover:border-background-300 cursor-pointer'
                        }`}
                        disabled={isDisabled}
                      >
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium mb-1 ${info.color}`}>
                          {info.label}
                        </span>
                        {isDisabled && <i className="ri-lock-line text-[10px] ml-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-1.5">
                  Plano
                </label>
                <select
                  value={addPlan}
                  onChange={(e) => setAddPlan(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
                >
                  {Object.entries(PLAN_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-background-100 dark:border-background-200">
              <button
                onClick={() => setAddModalOpen(false)}
                disabled={adding}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddUser}
                disabled={adding || !addEmail || !addPassword}
                className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-40 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer ml-auto flex items-center gap-2"
              >
                {adding ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <i className="ri-user-add-line" />
                    Criar usuário
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}